import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Building2,
  RefreshCw,
  Plus,
  Search,
  Filter,
  ChevronDown,
  Check,
  X,
  MoreVertical,
} from 'lucide-react';
import {
  getSubscriptions,
  getPlans,
  createSubscription,
  updateSubscription,
  cancelSubscription,
  reactivateSubscription,
  generateInvoice,
  Subscription,
  SubscriptionPlan,
  formatCurrency,
  getStatusColor,
  getStatusLabel,
} from '@/services/billingService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { useConfirmDialog } from '@/shared/components/ui/ConfirmDialog';

interface Institution {
  id: string;
  name: string;
  subdomain: string;
  status: string;
  city?: string;
}

export const SubscriptionsPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();
  const { confirm, Dialog: ConfirmDialog } = useConfirmDialog();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; showAbove: boolean } | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [newSubscription, setNewSubscription] = useState({
    tenantId: '',
    planId: '',
    trialDays: 14,
  });
  const [editForm, setEditForm] = useState({
    planId: '',
    businessName: '',
    ice: '',
    ifiscal: '',
    rc: '',
    billingAddress: '',
    billingCity: '',
    excludeStaff: false,
    excludeProfessors: false,
  });

  // Institution search dropdown state
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const selectedInstitution = useMemo(
    () => institutions.find((i) => i.id === newSubscription.tenantId),
    [institutions, newSubscription.tenantId]
  );

  // Filter institutions based on search
  const filteredInstitutions = useMemo(() => {
    if (!institutionSearch) return institutions;
    const search = institutionSearch.toLowerCase();
    return institutions.filter(
      (i) =>
        i.name.toLowerCase().includes(search) ||
        i.subdomain.toLowerCase().includes(search) ||
        i.city?.toLowerCase().includes(search)
    );
  }, [institutions, institutionSearch]);

  // Fetch institutions on mount
  const fetchInstitutions = useCallback(async () => {
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter only active/trial institutions that can have subscriptions
        setInstitutions(data.filter((i: Institution) => i.status !== 'INACTIVE'));
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subsData, plansData] = await Promise.all([
        getSubscriptions(statusFilter || undefined),
        getPlans(),
      ]);
      setSubscriptions(subsData);
      setPlans(plansData.filter(p => p.isActive));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
    fetchInstitutions();
  }, [fetchData, fetchInstitutions]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      await createSubscription(newSubscription);
      setShowCreateModal(false);
      setNewSubscription({ tenantId: '', planId: '', trialDays: 14 });
      fetchData();
    } catch (error) {
      console.error('Failed to create subscription:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCancelSubscription = async (tenantId: string) => {
    const confirmed = await confirm({
      title: 'Annuler l\'abonnement',
      message: 'Êtes-vous sûr de vouloir annuler cet abonnement ? L\'établissement perdra l\'accès à la plateforme.',
      confirmText: 'Annuler l\'abonnement',
      cancelText: 'Retour',
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await cancelSubscription(tenantId);
      success('Abonnement annulé', 'L\'abonnement a été annulé avec succès');
      fetchData();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      error('Erreur', 'Impossible d\'annuler l\'abonnement');
    }
  };

  const handleReactivate = async (tenantId: string) => {
    try {
      await reactivateSubscription(tenantId);
      fetchData();
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
    }
  };

  const handleGenerateInvoice = async (subscriptionId: string) => {
    try {
      await generateInvoice(subscriptionId);
      success('Facture générée', 'La facture a été générée avec succès');
      setOpenMenuId(null);
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      error('Erreur', 'Impossible de générer la facture');
    }
  };

  const handleOpenMenu = (id: string) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      setMenuPosition(null);
      return;
    }

    const button = menuButtonRefs.current[id];
    if (button) {
      const rect = button.getBoundingClientRect();
      const menuHeight = 200; // Approximate menu height
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < menuHeight && rect.top > menuHeight;

      setMenuPosition({
        top: showAbove ? rect.top : rect.bottom + 4,
        left: rect.right - 192, // 192px = menu width (w-48)
        showAbove,
      });
    }
    setOpenMenuId(id);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const openEditModal = (sub: Subscription) => {
    setSelectedSubscription(sub);
    setEditForm({
      planId: sub.planId,
      businessName: sub.businessName || '',
      ice: sub.ice || '',
      ifiscal: sub.ifiscal || '',
      rc: sub.rc || '',
      billingAddress: sub.billingAddress || '',
      billingCity: sub.billingCity || '',
      excludeStaff: sub.excludeStaff || false,
      excludeProfessors: sub.excludeProfessors || false,
    });
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubscription) return;

    try {
      setUpdating(true);
      await updateSubscription(selectedSubscription.tenantId, editForm);
      success('Abonnement mis à jour', 'Les modifications ont été enregistrées');
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      console.error('Failed to update subscription:', err);
      error('Erreur', 'Impossible de mettre à jour l\'abonnement');
    } finally {
      setUpdating(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        sub.tenant?.name?.toLowerCase().includes(query) ||
        sub.plan?.name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {ConfirmDialog}
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Abonnements</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gérez les abonnements des établissements
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} />
            Nouvel abonnement
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher par établissement ou plan..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Tous les statuts</option>
            <option value="ACTIVE">Actif</option>
            <option value="TRIAL">Essai</option>
            <option value="PAST_DUE">En retard</option>
            <option value="CANCELLED">Annulé</option>
            <option value="SUSPENDED">Suspendu</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELLED', 'SUSPENDED'].map((status) => {
          const count = subscriptions.filter((s) => s.status === status).length;
          return (
            <div
              key={status}
              className="bg-white rounded-lg border border-gray-100 p-4 text-center"
            >
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-sm text-gray-500">{getStatusLabel(status)}</p>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase">Établissement</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase">Utilisateurs</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase">Prochain paiement</th>
              <th className="px-6 py-3 text-center text-xs font-semibold uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredSubscriptions.map((sub) => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Building2 className="text-indigo-600" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{sub.tenant?.name || 'N/A'}</p>
                      {sub.tenant?.ice && (
                        <p className="text-xs text-gray-500">ICE: {sub.tenant.ice}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="font-medium text-gray-900">{sub.plan?.name || 'N/A'}</p>
                  <p className="text-sm text-gray-500">
                    {sub.plan && formatCurrency(sub.plan.basePrice)}/{getStatusLabel(sub.plan?.billingCycle || 'MONTHLY').toLowerCase()}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(sub.status)}`}>
                    {getStatusLabel(sub.status)}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-900">{sub.userCount}</td>
                <td className="px-6 py-4 text-gray-500">
                  {new Date(sub.nextPaymentDate).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center">
                    <button
                      ref={(el) => (menuButtonRefs.current[sub.id] = el)}
                      onClick={() => handleOpenMenu(sub.id)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredSubscriptions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  Aucun abonnement trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dropdown Menu - Fixed positioned */}
      {openMenuId && menuPosition && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeMenu}
          />
          <div
            className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
            style={{
              top: menuPosition.showAbove ? 'auto' : menuPosition.top,
              bottom: menuPosition.showAbove ? window.innerHeight - menuPosition.top + 4 : 'auto',
              left: menuPosition.left,
            }}
          >
            {(() => {
              const sub = subscriptions.find(s => s.id === openMenuId);
              if (!sub) return null;
              return (
                <>
                  <button
                    onClick={() => {
                      setSelectedSubscription(sub);
                      setShowDetailModal(true);
                      closeMenu();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Voir détails
                  </button>
                  <button
                    onClick={() => {
                      openEditModal(sub);
                      closeMenu();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => {
                      handleGenerateInvoice(sub.id);
                      closeMenu();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50"
                  >
                    Générer facture
                  </button>
                  {sub.status === 'CANCELLED' ? (
                    <button
                      onClick={() => {
                        handleReactivate(sub.tenantId);
                        closeMenu();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50"
                    >
                      Réactiver
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleCancelSubscription(sub.tenantId);
                        closeMenu();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                    >
                      Annuler
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouvel abonnement</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Établissement *
                </label>
                <div className="relative">
                  {/* Selected institution display or search input */}
                  <div
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 cursor-pointer bg-white"
                    onClick={() => setShowInstitutionDropdown(true)}
                  >
                    {selectedInstitution ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 size={16} className="text-indigo-600" />
                          <span className="font-medium">{selectedInstitution.name}</span>
                          {selectedInstitution.city && (
                            <span className="text-gray-500 text-sm">({selectedInstitution.city})</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setNewSubscription({ ...newSubscription, tenantId: '' });
                            setInstitutionSearch('');
                          }}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <X size={14} className="text-gray-400" />
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={institutionSearch}
                        onChange={(e) => {
                          setInstitutionSearch(e.target.value);
                          setShowInstitutionDropdown(true);
                        }}
                        onFocus={() => setShowInstitutionDropdown(true)}
                        placeholder="Rechercher un établissement..."
                        className="w-full outline-none bg-transparent"
                      />
                    )}
                  </div>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />

                  {/* Dropdown list */}
                  {showInstitutionDropdown && !selectedInstitution && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowInstitutionDropdown(false)}
                      />
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredInstitutions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            Aucun établissement trouvé
                          </div>
                        ) : (
                          filteredInstitutions.map((inst) => (
                            <button
                              key={inst.id}
                              type="button"
                              onClick={() => {
                                setNewSubscription({ ...newSubscription, tenantId: inst.id });
                                setInstitutionSearch('');
                                setShowInstitutionDropdown(false);
                              }}
                              className="w-full px-4 py-2.5 text-left hover:bg-indigo-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                            >
                              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Building2 size={16} className="text-indigo-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{inst.name}</p>
                                <p className="text-xs text-gray-500">
                                  {inst.subdomain}.nexedu.ma {inst.city && `• ${inst.city}`}
                                </p>
                              </div>
                              {newSubscription.tenantId === inst.id && (
                                <Check size={16} className="text-indigo-600 flex-shrink-0" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                {/* Hidden required input for form validation */}
                <input
                  type="hidden"
                  value={newSubscription.tenantId}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan *
                </label>
                <select
                  value={newSubscription.planId}
                  onChange={(e) => setNewSubscription({ ...newSubscription, planId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Sélectionner un plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {formatCurrency(plan.basePrice)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jours d'essai
                </label>
                <input
                  type="number"
                  value={newSubscription.trialDays}
                  onChange={(e) => setNewSubscription({ ...newSubscription, trialDays: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {creating ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Détails de l'abonnement</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Établissement</p>
                  <p className="font-medium">{selectedSubscription.tenant?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Plan</p>
                  <p className="font-medium">{selectedSubscription.plan?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedSubscription.status)}`}>
                    {getStatusLabel(selectedSubscription.status)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Utilisateurs</p>
                  <p className="font-medium">{selectedSubscription.userCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Début de période</p>
                  <p className="font-medium">
                    {new Date(selectedSubscription.currentPeriodStart).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fin de période</p>
                  <p className="font-medium">
                    {new Date(selectedSubscription.currentPeriodEnd).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Prochain paiement</p>
                  <p className="font-medium">
                    {new Date(selectedSubscription.nextPaymentDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                {selectedSubscription.trialEndsAt && (
                  <div>
                    <p className="text-sm text-gray-500">Fin d'essai</p>
                    <p className="font-medium">
                      {new Date(selectedSubscription.trialEndsAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
              </div>
              {/* Exclusion settings */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Paramètres de facturation</p>
                <div className="flex gap-4 text-sm">
                  <span className={selectedSubscription.excludeStaff ? 'text-orange-600' : 'text-green-600'}>
                    Personnel: {selectedSubscription.excludeStaff ? 'Exclu' : 'Inclus'}
                  </span>
                  <span className={selectedSubscription.excludeProfessors ? 'text-orange-600' : 'text-green-600'}>
                    Professeurs: {selectedSubscription.excludeProfessors ? 'Exclus' : 'Inclus'}
                  </span>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    openEditModal(selectedSubscription);
                  }}
                  className="px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                >
                  Modifier
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Modifier l'abonnement</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedSubscription.tenant?.name}
              </p>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-6">
              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  Les modifications s'appliqueront aux factures futures uniquement. L'historique des factures existantes est préservé.
                </p>
              </div>

              {/* Plan selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan d'abonnement
                </label>
                <select
                  value={editForm.planId}
                  onChange={(e) => setEditForm({ ...editForm, planId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {formatCurrency(plan.basePrice)}/{getStatusLabel(plan.billingCycle).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* User exclusion settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Utilisateurs à facturer
                </label>
                <div className="space-y-2">
                  <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                    <span className="font-medium">Toujours inclus :</span> Étudiants, Administration
                    <br />
                    <span className="font-medium">Toujours exclus :</span> Parents
                  </div>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!editForm.excludeStaff}
                      onChange={(e) => setEditForm({ ...editForm, excludeStaff: !e.target.checked })}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Inclure le personnel</p>
                      <p className="text-sm text-gray-500">Compter le staff dans la facturation</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!editForm.excludeProfessors}
                      onChange={(e) => setEditForm({ ...editForm, excludeProfessors: !e.target.checked })}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Inclure les professeurs</p>
                      <p className="text-sm text-gray-500">Compter les profs dans la facturation</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Business info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informations de facturation
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Raison sociale</label>
                    <input
                      type="text"
                      value={editForm.businessName}
                      onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })}
                      placeholder="Nom de l'entreprise pour la facture"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">ICE</label>
                    <input
                      type="text"
                      value={editForm.ice}
                      onChange={(e) => setEditForm({ ...editForm, ice: e.target.value })}
                      placeholder="15 chiffres"
                      maxLength={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">IF (Identifiant Fiscal)</label>
                    <input
                      type="text"
                      value={editForm.ifiscal}
                      onChange={(e) => setEditForm({ ...editForm, ifiscal: e.target.value })}
                      placeholder="Identifiant fiscal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">RC</label>
                    <input
                      type="text"
                      value={editForm.rc}
                      onChange={(e) => setEditForm({ ...editForm, rc: e.target.value })}
                      placeholder="Registre de commerce"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ville</label>
                    <input
                      type="text"
                      value={editForm.billingCity}
                      onChange={(e) => setEditForm({ ...editForm, billingCity: e.target.value })}
                      placeholder="Ville"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Adresse de facturation</label>
                    <input
                      type="text"
                      value={editForm.billingAddress}
                      onChange={(e) => setEditForm({ ...editForm, billingAddress: e.target.value })}
                      placeholder="Adresse complète"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {updating ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SubscriptionsPage;
